import { Component, OnInit, HostListener } from '@angular/core';
import {
  IonIcon, IonItem, IonLabel, IonInput, IonList, IonChip,
  IonToolbar, IonTitle, IonButtons, IonButton, IonSpinner,
  ModalController
} from '@ionic/angular/standalone';
import { NgClass } from '@angular/common';
import { format, parse, isAfter, startOfDay } from 'date-fns';
import { it } from 'date-fns/locale';
import { isBefore, isEqual } from 'date-fns';
import { finalize, switchMap, takeUntil, map } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { GlobalManagerFigliService } from '../../../services/global-manager-figli.service';
import { LoaderService } from '../../../services/loader.service';
import { NotificationService } from '../../../services/notification.service';
import { GlobalService } from '../../../services/global.service';
import {
  Studente, StudentiService, EventoCalendarioDTO,
  EventoCalendarioStudenteForm, EventoCalendarioType,
  EventoCalendarioDTOType,
  AddPatchRicaricaDTO, ConfigurazioneComuneView, GetRicaricaView, GetRicaricheResponseDTO
} from '../../../../api-clients/generated/services';
import { CommandAction } from '../../interfaces/command-action.interface';
import { ModalRoleEnum } from '../../enums/modal-enum';
import { CalendarActionFormComponent } from './calendar-action-form/calendar-action-form.component';
import { CommandParserService } from '../../services/command-parser/command-parser.service';
import { ParseResult, ParsedToken, INTENT_LABELS, isRemoveAction, isPaymentAction, isNavigationAction, formatEuro } from '../../services/command-parser/command-parser.model';
import { CurrencyEuroPipe } from '../../pipes/currency-euro.pipe';
import { PathEnum } from '../../enums/path-enum';
import { formatDateForDisplay } from '../../services/command-parser/date-parser';
import {
  getExcludedDays, isExcludedDay, getEventState, hasChiusura, getSospesoMessage
} from '../../services/command-parser/calendar-validators';
import { UnsubscribeOnDestroy } from '../../abstracts/unsubscribe-on-destroy';

import TipoPagamentoEnum = ConfigurazioneComuneView.TipoPagamentoEnum;
import StatoRicaricaManualeEnum = ConfigurazioneComuneView.StatoRicaricaManualeEnum;

type PaletteStep = 'search' | 'select-child' | 'action-form' | 'amount-form' | 'remove-form' | 'confirm';

export interface RemoveChip {
  evento: EventoCalendarioDTO;
  label: string;
  shortDate: string;
}

const EXAMPLE_CHIPS: { text: string; icon: string }[] = [
  { text: 'assenza domani', icon: 'calendar-outline' },
  { text: 'pasta bianco lunedì', icon: 'restaurant-outline' },
];

const CALENDAR_ACTIONS: CommandAction[] = [
  { id: 'assenza', label: 'Aggiungi Assenza', icon: 'calendar-outline', actionType: 'assenza',
    keywords: ['assente', 'malato', 'malattia', 'mancanza', 'non presente', 'giustifica', 'calendario'] },
  { id: 'pasta', label: 'Aggiungi Pasta in Bianco', icon: 'restaurant-outline', actionType: 'pasta-in-bianco',
    keywords: ['bianco', 'dieta', 'leggera', 'menu', 'pasto', 'speciale', 'calendario'] },
  { id: 'rimuovi-assenza', label: 'Rimuovi Assenza', icon: 'calendar-clear-outline', actionType: 'rimuovi-assenza',
    keywords: ['rimuovi', 'cancella', 'annulla', 'elimina', 'togli', 'calendario'] },
  { id: 'rimuovi-pasta', label: 'Rimuovi Pasta in Bianco', icon: 'close-circle-outline', actionType: 'rimuovi-pasta-in-bianco',
    keywords: ['rimuovi', 'cancella', 'annulla', 'elimina', 'togli', 'calendario'] },
];

const PAYMENT_ACTION: CommandAction = {
  id: 'genera-pagamento', label: 'Genera Pagamento', icon: 'card-outline', actionType: 'genera-pagamento',
  keywords: ['ricarica', 'ricariche', 'pagamento', 'pagamenti', 'bollettino', 'pagopa', 'euro']
};

const NAVIGATION_PAYMENT_ACTION: CommandAction = {
  id: 'vai-pagamenti', label: 'Vai ai Pagamenti', icon: 'wallet-outline', actionType: 'navigazione-pagamenti',
  keywords: ['pagamenti', 'ricariche', 'bollettino', 'pagopa']
};

function studenteHasRicaricheAttive(s: Studente): boolean {
  const config = s.scuola?.configurazioneComune;
  if (!config) return false;
  const tipo = config.tipoPagamento;
  const hasPagoPaRicariche = tipo === TipoPagamentoEnum.PAGOPARICARICHE
    || tipo === TipoPagamentoEnum.PAGOPARENDICONTAZIONEERICARICHE;
  const isManualActive = config.statoRicaricaManuale === StatoRicaricaManualeEnum.ATTIVA;
  return hasPagoPaRicariche && isManualActive;
}

function studenteHasPagoPa(s: Studente): boolean {
  const tipo = s.scuola?.configurazioneComune?.tipoPagamento;
  return !!tipo && tipo !== TipoPagamentoEnum.NONGESTITO && tipo !== TipoPagamentoEnum.MANUALE;
}

function buildActions(studenti: Studente[]): CommandAction[] {
  const actions: CommandAction[] = [...CALENDAR_ACTIONS];
  if (studenti.some(studenteHasRicaricheAttive)) {
    actions.push(PAYMENT_ACTION);
  }
  if (studenti.some(studenteHasPagoPa)) {
    actions.push(NAVIGATION_PAYMENT_ACTION);
  }
  return actions;
}

@Component({
  selector: 'app-command-palette',
  templateUrl: './command-palette.component.html',
  styleUrls: ['./command-palette.component.scss'],
  standalone: true,
  imports: [
    IonIcon, IonItem, IonLabel, IonInput, IonList, IonChip,
    IonToolbar, IonTitle, IonButtons, IonButton, IonSpinner,
    NgClass, CalendarActionFormComponent, CurrencyEuroPipe
  ]
})
export class CommandPaletteComponent extends UnsubscribeOnDestroy implements OnInit {
  readonly EXAMPLE_CHIPS = EXAMPLE_CHIPS;

  step: PaletteStep = 'search';
  searchText = '';
  availableActions: CommandAction[] = [];
  filteredActions: CommandAction[] = [];
  selectedAction: CommandAction | null = null;
  activeIndex = -1;

  studenti: Studente[] = [];
  selectedStudente: Studente | null = null;

  // NLP parser state
  parseResult: ParseResult | null = null;
  prefilledDateInizio: string | null = null;
  prefilledDateFine: string | null = null;
  private nluDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  nluLoading = false;

  // Remove-form step state
  removeChips: RemoveChip[] = [];
  selectedRemoveChip: RemoveChip | null = null;
  loadingRemoveEvents = false;

  // Amount-form step state
  amountInput: number | null = null;
  residuoHint: number | null = null;

  // Confirm step state
  confirmSaving = false;
  loadingValidation = false;
  confirmWarnings: string[] = [];
  canConfirm = false;
  confirmRemoveEventId: string | null = null;

  constructor(
    private modalController: ModalController,
    private managerFigli: GlobalManagerFigliService,
    private parser: CommandParserService,
    private studentiService: StudentiService,
    private loaderService: LoaderService,
    private notificationService: NotificationService,
    private globalService: GlobalService,
    private router: Router
  ) {
    super();
  }

  override ngOnDestroy() {
    if (this.nluDebounceTimer) {
      clearTimeout(this.nluDebounceTimer);
    }
    super.ngOnDestroy();
  }

  ngOnInit() {
    this.studenti = this.managerFigli.opzioniStudenti;
    this.availableActions = buildActions(this.studenti);
    this.filteredActions = [...this.availableActions];
    if (this.studenti.length === 1) {
      this.selectedStudente = this.studenti[0];
    }
  }

  // --- Search step ---

  get resultsAnnouncement(): string {
    const count = this.filteredActions.length;
    return count === 0
      ? 'Nessuna azione trovata'
      : `${count} ${count === 1 ? 'azione disponibile' : 'azioni disponibili'}`;
  }

  get previewAnnouncement(): string {
    if (!this.parseResult || this.parseResult.confidence === 'none') return '';
    const parts: string[] = [];
    for (const token of this.parseResult.tokens) {
      if (token.type === 'noise') continue;
      const typeLabels: Record<string, string> = { intent: 'Azione', figlio: 'Studente', data: 'Data', importo: 'Importo' };
      const typeLabel = typeLabels[token.type] ?? token.type;
      parts.push(`${typeLabel}: ${token.resolved}`);
    }
    if (this.parseResult.confidence === 'full') {
      parts.push('Premi Invio per confermare');
    }
    return parts.join('. ');
  }

  private static readonly REMOVE_VERBS = new Set([
    'rimuovi', 'cancella', 'togli', 'annulla', 'elimina',
  ]);

  get missingSlotHint(): string | null {
    if (!this.parseResult) return null;

    // When no intent is detected, check if user typed a remove verb without object
    if (this.parseResult.confidence === 'none' || (!this.parseResult.intent && this.parseResult.confidence === 'partial')) {
      const noiseTokens = this.parseResult.tokens
        .filter(t => t.type === 'noise')
        .map(t => t.raw.toLowerCase());
      if (noiseTokens.some(t => CommandPaletteComponent.REMOVE_VERBS.has(t))) {
        return 'Specifica: cosa (es. "assenza", "pasta bianco")';
      }
      return null;
    }

    if (this.parseResult.confidence !== 'partial') return null;
    if (!this.parseResult.intent) return null;

    const { intent, figlio, data, importo } = this.parseResult;
    const needsChild = !figlio && this.studenti.length > 1;
    const childExample = this.studenti.length > 0
      ? `"${this.studenti[0].nome}"`
      : '"Mario"';

    if (isPaymentAction(intent)) {
      if (needsChild) return `Aggiungi: chi (es. ${childExample})`;
      if (importo === null) return 'Aggiungi: importo (es. "50")';
      return null;
    }

    if (isNavigationAction(intent)) return null;

    if (isRemoveAction(intent)) {
      if (needsChild) return `Aggiungi: chi (es. ${childExample})`;
      return null;
    }

    // assenza, pasta-in-bianco
    if (!data) return 'Aggiungi: quando (es. "domani", "dal 14 al 18")';
    if (needsChild) return `Aggiungi: chi (es. ${childExample})`;
    return null;
  }

  applyExample(text: string): void {
    this.searchText = text;
    this.onSearchChange(text);
  }

  onSearchChange(value: string) {
    this.searchText = value;

    // Level 1: keyword parse (sync, instant)
    this.parseResult = this.parser.parseLive(value, this.studenti);

    if (this.parseResult.intent) {
      this.nluLoading = false;
      this.filteredActions = this.availableActions.filter(a =>
        a.actionType === this.parseResult!.intent
      );
    } else {
      const term = value.toLowerCase();
      this.filteredActions = this.availableActions.filter(a =>
        a.label.toLowerCase().includes(term) ||
        a.keywords?.some(k => k.includes(term))
      );

      // Level 2: NLU fallback with 300ms debounce
      this.scheduleNluLive(value, this.parseResult);
    }
    this.activeIndex = -1;
  }

  private scheduleNluLive(value: string, currentResult: ParseResult) {
    if (this.nluDebounceTimer) {
      clearTimeout(this.nluDebounceTimer);
    }
    if (!value || !value.trim()) {
      this.nluLoading = false;
      return;
    }

    this.nluLoading = true;
    this.nluDebounceTimer = setTimeout(async () => {
      if (this.step !== 'search' || this.searchText !== value) {
        this.nluLoading = false;
        return;
      }

      const matched = await this.parser.tryNluFallback(currentResult);
      this.nluLoading = false;

      if (!matched) return;

      // Still on same input?
      if (this.step !== 'search' || this.searchText !== value) return;

      this.parseResult = currentResult;
      this.filteredActions = this.availableActions.filter(a => a.actionType === currentResult.intent);
    }, 300);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (this.step === 'search') {
      this.handleSearchKeyboard(event);
    }
  }

  private handleSearchKeyboard(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeIndex = Math.min(this.activeIndex + 1, this.filteredActions.length - 1);
        if (this.activeIndex < 0) this.activeIndex = 0;
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, 0);
        break;
      case 'Enter':
        event.preventDefault();
        this.handleEnter();
        break;
    }
  }

  async handleEnter() {
    if (!this.searchText?.trim()) return;

    const result = await this.parser.parse(this.searchText, this.studenti);

    if (isNavigationAction(result.intent)) {
      this.navigateToPayments();
      return;
    }

    if (result.confidence === 'full') {
      this.parseResult = result;
      this.goToConfirm(result);
    } else if (result.intent) {
      this.parseResult = result;
      this.selectActionFromParse(result);
    } else if (this.filteredActions[this.activeIndex]) {
      this.selectAction(this.filteredActions[this.activeIndex]);
    } else {
      this.notificationService.openToast({
        message: 'Non ho capito la richiesta. Prova con "assenza domani" o seleziona un\'azione dalla lista.',
        color: 'warning'
      });
    }
  }

  private goToConfirm(result: ParseResult) {
    this.selectedAction = this.availableActions.find(a => a.actionType === result.intent) ?? null;
    this.selectedStudente = result.figlio;
    this.confirmWarnings = [];
    this.canConfirm = false;
    this.loadingValidation = false;
    this.confirmRemoveEventId = null;

    this.step = 'confirm';
    if (isRemoveAction(result.intent)) {
      this.runRemoveConfirmValidation(result);
    } else if (isPaymentAction(result.intent)) {
      this.runPaymentValidation();
    } else {
      this.runConfirmValidation();
    }
  }

  private runConfirmValidation() {
    const warnings: string[] = [];

    // 1. Synchronous checks
    const sospesoMsg = getSospesoMessage(this.selectedStudente);
    if (sospesoMsg) {
      warnings.push(sospesoMsg);
      this.confirmWarnings = warnings;
      this.canConfirm = false;
      return; // Don't call API if suspended
    }

    if (this.parseResult?.data) {
      const data = this.parseResult.data;

      // Inizio > fine
      if (data.fine && isBefore(data.fine, data.inizio) && !isEqual(data.fine, data.inizio)) {
        warnings.push('La data di inizio non può essere successiva alla data di fine');
        this.confirmWarnings = warnings;
        this.canConfirm = false;
        return;
      }

      // Weekly closure days
      const excludedDays = getExcludedDays(this.selectedStudente);
      if (isExcludedDay(data.inizio, excludedDays)) {
        warnings.push('La data di inizio cade in un giorno di chiusura (scuola chiusa)');
      }
      if (data.fine && !isEqual(data.fine, data.inizio) && isExcludedDay(data.fine, excludedDays)) {
        warnings.push('La data di fine cade in un giorno di chiusura (scuola chiusa)');
      }

      if (warnings.length > 0) {
        this.confirmWarnings = warnings;
        this.canConfirm = false;
        return;
      }
    }

    // 2. Async checks — load events from API
    const studenteId = this.selectedStudente?.id;
    if (!studenteId || !this.parseResult?.data) {
      this.canConfirm = false;
      return;
    }

    this.loadingValidation = true;
    this.studentiService.getEventiCalendario(studenteId).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.loadingValidation = false)
    ).subscribe({
      next: (eventi) => this.validateWithEvents(eventi),
      error: () => {
        this.confirmWarnings = ['Errore durante la verifica. Riprovare più tardi.'];
        this.canConfirm = false;
      }
    });
  }

  private runRemoveConfirmValidation(result: ParseResult) {
    const studenteId = this.selectedStudente?.id;
    if (!studenteId || !result.data) {
      this.canConfirm = false;
      return;
    }

    const targetType = result.intent === 'rimuovi-assenza'
      ? EventoCalendarioDTOType.ASSENZA
      : EventoCalendarioDTOType.PASTAINBIANCO;

    const targetDateFmt = format(result.data.inizio, 'dd/MM/yyyy');

    this.loadingValidation = true;
    this.studentiService.getEventiCalendario(studenteId).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.loadingValidation = false)
    ).subscribe({
      next: (eventi) => {
        const match = eventi.find(e => e.tipo === targetType && e.data === targetDateFmt && e.id);
        if (match) {
          this.confirmRemoveEventId = match.id!;
          this.confirmWarnings = [];
          this.canConfirm = true;
        } else {
          const label = result.intent === 'rimuovi-assenza' ? 'assenza' : 'pasta in bianco';
          this.confirmWarnings = [`Nessuna ${label} trovata per il ${targetDateFmt}`];
          this.canConfirm = false;
        }
      },
      error: () => {
        this.confirmWarnings = ['Errore durante la verifica. Riprovare più tardi.'];
        this.canConfirm = false;
      }
    });
  }

  private validateWithEvents(eventi: EventoCalendarioDTO[]) {
    const warnings: string[] = [];
    const data = this.parseResult!.data!;
    const dataInizioFmt = format(data.inizio, 'dd/MM/yyyy');

    // Chiusura scolastica on start date
    if (hasChiusura(dataInizioFmt, eventi)) {
      warnings.push('Giorno di chiusura scolastica, impossibile inserire eventi');
    }

    // Chiusura scolastica on end date (range)
    if (data.fine && !isEqual(data.fine, data.inizio)) {
      const dataFineFmt = format(data.fine, 'dd/MM/yyyy');
      if (hasChiusura(dataFineFmt, eventi)) {
        warnings.push('La data di fine cade in un giorno di chiusura scolastica');
      }
    }

    // Already has event on start date
    const stateInizio = getEventState(dataInizioFmt, eventi);
    if (stateInizio === 'assenza') {
      warnings.push('Assenza già presente per questa data');
    } else if (stateInizio === 'pasta-in-bianco') {
      warnings.push('Pasta in bianco già presente per questa data');
    }

    this.confirmWarnings = warnings;
    this.canConfirm = warnings.length === 0;
  }

  private selectActionFromParse(result: ParseResult) {
    this.selectedAction = this.availableActions.find(a => a.actionType === result.intent) ?? null;

    if (isNavigationAction(result.intent)) {
      this.navigateToPayments();
      return;
    }

    if (result.figlio) {
      this.selectedStudente = result.figlio;
    }

    // Pre-fill dates if available
    if (result.data) {
      this.prefilledDateInizio = format(result.data.inizio, 'yyyy-MM-dd');
      this.prefilledDateFine = result.data.fine
        ? format(result.data.fine, 'yyyy-MM-dd')
        : this.prefilledDateInizio;
    } else {
      this.prefilledDateInizio = null;
      this.prefilledDateFine = null;
    }

    // Pre-fill amount if available
    if (result.importo !== null) {
      this.amountInput = result.importo;
    }

    if (this.selectedStudente) {
      this.goToActionStep();
    } else if (this.studenti.length === 1) {
      this.selectedStudente = this.studenti[0];
      this.goToActionStep();
    } else {
      this.step = 'select-child';
    }
  }

  selectAction(action: CommandAction) {
    this.selectedAction = action;
    this.prefilledDateInizio = null;
    this.prefilledDateFine = null;
    this.amountInput = null;

    if (isNavigationAction(action.actionType)) {
      this.navigateToPayments();
      return;
    }

    if (this.studenti.length === 1) {
      this.goToActionStep();
    } else {
      this.step = 'select-child';
    }
  }

  selectChild(studente: Studente) {
    if (isPaymentAction(this.selectedAction?.actionType ?? null) && !studenteHasRicaricheAttive(studente)) {
      this.notificationService.openToast({
        message: `Il servizio di ricarica non è attivo per ${studente.nome}`,
        color: 'warning'
      });
      return;
    }
    this.selectedStudente = studente;
    this.goToActionStep();
  }

  private goToActionStep() {
    if (isRemoveAction(this.selectedAction?.actionType ?? null)) {
      this.loadRemoveEvents();
    } else if (isPaymentAction(this.selectedAction?.actionType ?? null)) {
      if (this.amountInput !== null && this.amountInput > 0) {
        // Amount already extracted from text — go to confirm
        this.buildPaymentParseResult();
        this.goToConfirm(this.parseResult!);
      } else {
        this.prepareAmountForm();
        this.step = 'amount-form';
      }
    } else {
      this.step = 'action-form';
    }
  }

  // --- Confirm step ---

  get confirmActionLabel(): string {
    if (!this.parseResult?.intent) return '';
    return INTENT_LABELS[this.parseResult.intent];
  }

  get confirmStudenteName(): string {
    return this.selectedStudente
      ? `${this.selectedStudente.nome ?? ''} ${this.selectedStudente.cognome ?? ''}`.trim()
      : '';
  }

  get confirmDateLabel(): string {
    if (!this.parseResult?.data) return '';
    const d = this.parseResult.data;
    if (d.fine) {
      return `${formatDateForDisplay(d.inizio)} - ${formatDateForDisplay(d.fine)}`;
    }
    return formatDateForDisplay(d.inizio);
  }

  get confirmAmountLabel(): string {
    if (this.parseResult?.importo == null) return '';
    return formatEuro(this.parseResult.importo);
  }

  get isPaymentConfirm(): boolean {
    return isPaymentAction(this.selectedAction?.actionType ?? null);
  }

  get confirmFuzzyHint(): string | null {
    if (!this.parseResult?.childMatch || this.parseResult.childMatch.status !== 'fuzzy') return null;
    return this.parseResult.childMatch.originalInput ?? null;
  }

  confirmSave() {
    if (!this.canConfirm) return;
    if (!this.selectedAction || !this.selectedStudente || !this.parseResult?.data) return;
    if (this.confirmSaving) return;

    const studenteId = this.selectedStudente.id!;
    const dataInizioFmt = format(this.parseResult.data.inizio, 'dd/MM/yyyy');
    const dataFineFmt = this.parseResult.data.fine
      ? format(this.parseResult.data.fine, 'dd/MM/yyyy')
      : dataInizioFmt;

    const tipoEvento = this.selectedAction.actionType === 'assenza'
      ? EventoCalendarioType.ASSENZA
      : EventoCalendarioType.PASTAINBIANCO;

    const body: EventoCalendarioStudenteForm = {
      data_inizio: dataInizioFmt,
      data_fine: dataFineFmt,
      tipo_evento: tipoEvento,
    };

    this.confirmSaving = true;
    this.loaderService.showLoaderObserve$().pipe(
      switchMap(() => this.studentiService.addEventoCalendario(body, studenteId)),
      takeUntil(this.destroy$),
      finalize(() => {
        this.loaderService.hideLoader();
        this.confirmSaving = false;
      })
    ).subscribe({
      next: () => {
        this.loaderService.hideLoader();
        const label = this.selectedAction!.actionType === 'assenza'
          ? 'Assenza salvata correttamente'
          : 'Pasta in bianco salvata correttamente';
        this.notificationService.openToast({ message: label, color: 'success' });
        this.globalService.nextOnRefreshCalendar();
        this.modalController.dismiss(null, ModalRoleEnum.CONFIRM);
      },
      error: (error: HttpErrorResponse) => {
        this.notificationService.openToast({
          message: error.error?.message || 'Errore durante il salvataggio. Riprovare più tardi.',
          color: 'danger'
        });
      }
    });
  }

  // --- Amount-form step ---

  private prepareAmountForm(prefill?: number | null) {
    const residuoRaw = Number(this.selectedStudente?.portafogli?.residuo ?? 0);
    this.residuoHint = residuoRaw < 0 && isFinite(residuoRaw) ? Math.abs(residuoRaw) : null;
    this.amountInput = prefill ?? null;
  }

  onAmountChange(value: string) {
    const parsed = parseFloat(value.replace(',', '.'));
    this.amountInput = isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  confirmAmount() {
    if (!this.amountInput || this.amountInput <= 0) return;
    this.buildPaymentParseResult();
    this.goToConfirm(this.parseResult!);
  }

  private buildPaymentParseResult() {
    if (!this.parseResult) {
      this.parseResult = {
        intent: 'genera-pagamento',
        figlio: this.selectedStudente,
        figlioAutoSelected: this.studenti.length === 1,
        childMatch: this.selectedStudente ? { studente: this.selectedStudente, status: 'matched' } : null,
        data: null,
        importo: this.amountInput,
        confidence: 'full',
        tokens: [],
      };
    } else {
      this.parseResult.importo = this.amountInput;
      this.parseResult.intent = 'genera-pagamento';
      if (this.parseResult.figlio && this.parseResult.importo) {
        this.parseResult.confidence = 'full';
      }
    }
  }

  // --- Payment validation ---

  private runPaymentValidation() {
    const warnings: string[] = [];
    const studente = this.selectedStudente;

    if (!studente) {
      this.canConfirm = false;
      return;
    }

    const config = studente.scuola?.configurazioneComune;
    const statoRicarica = config?.statoRicaricaManuale;

    // Gate 1: statoRicaricaManuale must be ATTIVA
    if (statoRicarica !== StatoRicaricaManualeEnum.ATTIVA) {
      warnings.push('Non è possibile generare pagamenti manuali in questo momento');
      this.confirmWarnings = warnings;
      this.canConfirm = false;
      return;
    }

    // Gate 2: amount > 0
    if (!this.parseResult?.importo || this.parseResult.importo <= 0) {
      warnings.push('Importo non valido');
      this.confirmWarnings = warnings;
      this.canConfirm = false;
      return;
    }

    // Gate 3: if REND_E_RICARICHE, check pending bollettini via API
    const tipo = config?.tipoPagamento;
    if (tipo === TipoPagamentoEnum.PAGOPARENDICONTAZIONEERICARICHE) {
      this.loadingValidation = true;
      this.studentiService.getRicariche(studente.id!).pipe(
        map(response => response.ricariche ?? []),
        takeUntil(this.destroy$),
        finalize(() => this.loadingValidation = false)
      ).subscribe({
        next: (ricariche) => {
          const hasPending = ricariche.some(r =>
            r.statoInterno === GetRicaricaView.StatoInternoEnum.INATTESAPAGAMENTO
          );
          if (hasPending) {
            this.confirmWarnings = ['È già presente un pagamento in sospeso. Completalo prima di generarne uno nuovo.'];
            this.canConfirm = false;
          } else {
            this.confirmWarnings = [];
            this.canConfirm = true;
          }
        },
        error: () => {
          this.confirmWarnings = ['Errore durante la verifica. Riprovare più tardi.'];
          this.canConfirm = false;
        }
      });
    } else {
      this.confirmWarnings = [];
      this.canConfirm = true;
    }
  }

  // --- Payment execution ---

  confirmSavePayment() {
    if (!this.canConfirm || this.confirmSaving) return;
    if (!this.selectedStudente?.id || !this.parseResult?.importo) return;

    const studenteId = this.selectedStudente.id;
    const body: AddPatchRicaricaDTO = {
      idStudente: studenteId,
      valore: this.parseResult.importo,
      dataPagamento: format(new Date(), 'dd/MM/yyyy')
    };

    this.confirmSaving = true;
    this.loaderService.showLoaderObserve$().pipe(
      switchMap(() => this.studentiService.addRicarica(body, studenteId)),
      takeUntil(this.destroy$),
      finalize(() => {
        this.loaderService.hideLoader();
        this.confirmSaving = false;
      })
    ).subscribe({
      next: async () => {
        this.notificationService.openToast({ message: 'Pagamento generato correttamente', color: 'success' });
        await this.modalController.dismiss(null, ModalRoleEnum.CONFIRM);
        this.router.navigate([PathEnum.PAGES + PathEnum.RICARICHE]);
      },
      error: (error: HttpErrorResponse) => {
        this.notificationService.openToast({
          message: error.error?.message || 'Errore durante la generazione del pagamento. Riprovare più tardi.',
          color: 'danger'
        });
      }
    });
  }

  // --- Navigation ---

  private async navigateToPayments() {
    await this.modalController.dismiss(null, ModalRoleEnum.CANCEL);
    this.router.navigate([PathEnum.PAGES + PathEnum.RICARICHE]);
  }

  confirmModify() {
    if (this.isRemoveConfirm) {
      this.loadRemoveEvents();
      return;
    }
    if (this.isPaymentConfirm) {
      this.prepareAmountForm(this.parseResult?.importo);
      this.step = 'amount-form';
      return;
    }
    if (this.parseResult?.data) {
      this.prefilledDateInizio = format(this.parseResult.data.inizio, 'yyyy-MM-dd');
      this.prefilledDateFine = this.parseResult.data.fine
        ? format(this.parseResult.data.fine, 'yyyy-MM-dd')
        : this.prefilledDateInizio;
    }
    this.step = 'action-form';
  }

  // --- Remove-form step ---

  private loadRemoveEvents() {
    const studenteId = this.selectedStudente?.id;
    if (!studenteId) return;

    this.removeChips = [];
    this.selectedRemoveChip = null;
    this.loadingRemoveEvents = true;
    this.step = 'remove-form';

    const targetType = this.selectedAction?.actionType === 'rimuovi-assenza'
      ? EventoCalendarioDTOType.ASSENZA
      : EventoCalendarioDTOType.PASTAINBIANCO;

    this.studentiService.getEventiCalendario(studenteId).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.loadingRemoveEvents = false)
    ).subscribe({
      next: (eventi) => {
        const today = startOfDay(new Date());
        const futureEvents = eventi
          .filter(e => e.tipo === targetType && e.data && e.id)
          .filter(e => {
            const eventDate = parse(e.data!, 'dd/MM/yyyy', new Date());
            return isAfter(eventDate, today) || isEqual(eventDate, today);
          })
          .sort((a, b) => {
            const da = parse(a.data!, 'dd/MM/yyyy', new Date());
            const db = parse(b.data!, 'dd/MM/yyyy', new Date());
            return da.getTime() - db.getTime();
          })
          .slice(0, 6);

        if (futureEvents.length === 0) {
          const label = this.selectedAction?.actionType === 'rimuovi-assenza' ? 'assenza' : 'pasta in bianco';
          const nome = this.selectedStudente?.nome ?? '';
          this.notificationService.openToast({
            message: `Nessuna ${label} futura per ${nome}`,
            color: 'warning'
          });
          this.goBackToSearch();
          return;
        }

        this.removeChips = futureEvents.map(e => this.buildRemoveChip(e));
      },
      error: () => {
        this.notificationService.openToast({
          message: 'Errore durante il caricamento degli eventi',
          color: 'danger'
        });
        this.goBackToSearch();
      }
    });
  }

  private buildRemoveChip(evento: EventoCalendarioDTO): RemoveChip {
    const eventDate = parse(evento.data!, 'dd/MM/yyyy', new Date());
    const today = startOfDay(new Date());
    const tomorrow = startOfDay(new Date());
    tomorrow.setDate(tomorrow.getDate() + 1);

    let label: string;
    if (isEqual(eventDate, today)) {
      label = 'Oggi';
    } else if (isEqual(eventDate, tomorrow)) {
      label = 'Domani';
    } else {
      const dayNames = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
      label = dayNames[eventDate.getDay()];
    }

    const shortDate = format(eventDate, 'd MMM', { locale: it });

    return { evento, label, shortDate };
  }

  selectRemoveChip(chip: RemoveChip) {
    this.selectedRemoveChip = chip;
  }

  get removeFormQuestion(): string {
    const nome = this.selectedStudente?.nome ?? '';
    if (this.selectedAction?.actionType === 'rimuovi-assenza') {
      return `Quale assenza di ${nome} vuoi rimuovere?`;
    }
    return `Quale pasta in bianco di ${nome} vuoi rimuovere?`;
  }

  get removeButtonLabel(): string {
    return this.selectedAction?.actionType === 'rimuovi-assenza' ? 'Rimuovi Assenza' : 'Rimuovi Pasta in Bianco';
  }

  confirmRemoveFromForm() {
    if (!this.selectedRemoveChip || !this.selectedStudente?.id) return;

    const evento = this.selectedRemoveChip.evento;
    this.confirmRemoveEventId = evento.id!;

    // Build a parseResult-like state for the confirm step display
    const eventDate = parse(evento.data!, 'dd/MM/yyyy', new Date());
    this.parseResult = {
      intent: this.selectedAction!.actionType,
      figlio: this.selectedStudente,
      figlioAutoSelected: false,
      childMatch: { studente: this.selectedStudente!, status: 'matched' },
      data: { inizio: eventDate },
      importo: null,
      confidence: 'full',
      tokens: [],
    };

    this.confirmWarnings = [];
    this.canConfirm = true;
    this.loadingValidation = false;
    this.step = 'confirm';
  }

  // --- Confirm step (remove) ---

  confirmRemove() {
    if (!this.confirmRemoveEventId || !this.selectedStudente?.id) return;
    if (this.confirmSaving) return;

    this.confirmSaving = true;
    this.loaderService.showLoaderObserve$().pipe(
      switchMap(() => this.studentiService.removeEventoCalendario(
        this.selectedStudente!.id!,
        this.confirmRemoveEventId!
      )),
      takeUntil(this.destroy$),
      finalize(() => {
        this.loaderService.hideLoader();
        this.confirmSaving = false;
      })
    ).subscribe({
      next: () => {
        this.loaderService.hideLoader();
        const label = this.selectedAction!.actionType === 'rimuovi-assenza'
          ? 'Assenza rimossa correttamente'
          : 'Pasta in bianco rimossa correttamente';
        this.notificationService.openToast({ message: label, color: 'success' });
        this.globalService.nextOnRefreshCalendar();
        this.modalController.dismiss(null, ModalRoleEnum.CONFIRM);
      },
      error: (error: HttpErrorResponse) => {
        this.notificationService.openToast({
          message: error.error?.message || 'Errore durante la rimozione. Riprovare più tardi.',
          color: 'danger'
        });
      }
    });
  }

  get isRemoveConfirm(): boolean {
    return isRemoveAction(this.selectedAction?.actionType ?? null);
  }

  // --- Navigation ---

  goBackToSearch() {
    this.step = 'search';
    this.selectedAction = null;
    this.prefilledDateInizio = null;
    this.prefilledDateFine = null;
    this.amountInput = null;
    this.residuoHint = null;
    this.selectedStudente = this.studenti.length === 1 ? this.studenti[0] : null;
  }

  goBackToSelectChild() {
    this.step = 'select-child';
    this.selectedStudente = null;
  }

  onFormSaved() {
    this.globalService.nextOnRefreshCalendar();
    this.modalController.dismiss(null, ModalRoleEnum.CONFIRM);
  }

  onFormBack() {
    if (this.studenti.length > 1) {
      this.goBackToSelectChild();
    } else {
      this.goBackToSearch();
    }
  }

  cancel() {
    this.modalController.dismiss(null, ModalRoleEnum.CANCEL);
  }

  // --- Token pill helpers ---

  getTokenColor(token: ParsedToken): string {
    if (token.status === 'fuzzy') return 'warning';
    if (token.type === 'figlio') return 'secondary';
    if (token.type === 'intent' || token.type === 'data' || token.type === 'importo') return 'success';
    return 'medium';
  }

  getTokenIcon(token: ParsedToken): string {
    switch (token.type) {
      case 'intent': return 'flash-outline';
      case 'figlio': return 'person-outline';
      case 'data': return 'calendar-outline';
      case 'importo': return 'card-outline';
      default: return 'help-outline';
    }
  }

  getTokenAriaLabel(token: ParsedToken): string {
    const typeLabels: Record<string, string> = {
      intent: 'Azione', figlio: 'Studente', data: 'Data', importo: 'Importo'
    };
    const typeLabel = typeLabels[token.type] ?? token.type;
    const fuzzyHint = token.status === 'fuzzy' ? `, corretto da "${token.raw}"` : '';
    return `${typeLabel}: ${token.resolved}${fuzzyHint}`;
  }
}
